import { MOCK_MEMBERS } from '@/features/marketing/mockData/members'

/**
 * Check-in two-column mockup. "X / Y confirmed" header + two side-
 * by-side stacks of entrant avatars (checked-in / awaiting).
 * Mirrors the real Stadium CheckInPhase visual but tighter for the
 * marketing surface — 4 / 8 confirmed reads as "actively in
 * progress" rather than empty or full.
 */
export function CheckInMockup() {
  // 8-team mock — first 4 from the roster are checked in, next 4
  // awaiting. Picks intentionally so the visual has a mix of
  // verified / unverified players in each column.
  const all = MOCK_MEMBERS.slice(0, 8)
  const checkedIn = all.slice(0, 4)
  const awaiting = all.slice(4)

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="font-display text-[10px] font-bold tracking-[0.22em] text-primary uppercase">
            Check-in
          </p>
          <p className="font-display mt-1 text-3xl font-black leading-none">
            <span className="text-success">{checkedIn.length}</span>
            <span className="text-fg-subtle"> / {all.length}</span>
          </p>
          <p className="font-display mt-1 text-[10px] tracking-[0.18em] text-fg-muted uppercase">
            Confirmed
          </p>
        </div>
        <span className="font-display rounded-full border border-warning/40 bg-warning/10 px-3 py-1 text-[11px] font-extrabold tracking-[0.2em] text-warning uppercase">
          4m left
        </span>
      </div>

      {/* Two columns */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <Column title="Checked in" entrants={checkedIn} state="in" />
        <Column title="Awaiting" entrants={awaiting} state="out" />
      </div>
    </div>
  )
}

function Column({
  title,
  entrants,
  state,
}: {
  title: string
  entrants: typeof MOCK_MEMBERS
  state: 'in' | 'out'
}) {
  const accent =
    state === 'in'
      ? 'border-success/40 text-success'
      : 'border-fg-subtle/40 text-fg-subtle'
  return (
    <div>
      <p
        className={`font-display flex items-center justify-between text-[10px] font-bold tracking-[0.2em] uppercase ${accent}`}
      >
        <span>{title}</span>
        <span>{entrants.length}</span>
      </p>
      <div className="mt-2 space-y-1.5">
        {entrants.map((m) => (
          <EntrantTile key={m.userId} entrant={m} state={state} />
        ))}
      </div>
    </div>
  )
}

function EntrantTile({
  entrant,
  state,
}: {
  entrant: (typeof MOCK_MEMBERS)[number]
  state: 'in' | 'out'
}) {
  const isIn = state === 'in'
  return (
    <div
      className="flex items-center gap-2 rounded-lg border px-2 py-1.5"
      style={{
        borderColor: isIn ? 'rgba(0,255,136,0.4)' : 'var(--color-border, #2a2a4d)',
        backgroundColor: isIn ? 'rgba(0,255,136,0.06)' : 'transparent',
        opacity: isIn ? 1 : 0.7,
      }}
    >
      <img
        src={entrant.avatarUrl}
        alt=""
        className="size-6 shrink-0 rounded-full border border-border"
        loading="lazy"
      />
      <span className="font-display flex-1 truncate text-[11px] font-semibold text-fg">
        {entrant.displayName}
      </span>
      {isIn && <span className="text-[10px] font-extrabold text-success">✓</span>}
    </div>
  )
}
